import { useQueryClient, type MutationFunction } from "@tanstack/react-query";
import type { Currency } from "@/lib/domain/Currency";
import type { PriceSource } from "@/lib/domain/PriceSource";
import { paginatedPrepend, useHTTP, useHTTPMutation, type HTTPResponse } from "@/lib/http";
import type { PriceAlertDetailsModel } from "@/features/priceAlerts/api/models/PriceAlertDetailsModel";
import type { PriceAlertMetric } from "@/features/priceAlerts/PriceAlertMetric";
import type { PriceAlertStep } from "@/features/priceAlerts/PriceAlertStep";
import type { PriceAlertTarget } from "@/features/priceAlerts/PriceAlertTarget";

export interface PriceAlertCreateRequestModel {
	readonly targetType: PriceAlertTarget;
	readonly targetId: string;
	readonly metricType: PriceAlertMetric;
	readonly metric: number;
	readonly currency: Currency;
	readonly step: PriceAlertStep | null;
	readonly priceSource: PriceSource;
}

export const usePriceAlertCreate = () => {
	const http = useHTTP("Required");
	const queryClient = useQueryClient();

	const mutationFn: MutationFunction<HTTPResponse<PriceAlertDetailsModel>, PriceAlertCreateRequestModel> = requestModel => {
		return http<PriceAlertDetailsModel, PriceAlertCreateRequestModel>({
			method: "POST",
			url: "/price-alert",
			body: requestModel
		});
	};

	return useHTTPMutation({
		mutationFn,
		onSuccess: (data) => {
			queryClient.setQueryData(["priceAlertDetails"], paginatedPrepend(data.body));
		}
	});
};
